import { useEffect, useMemo, useState } from "react";
import { Input } from "antd";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import "./index.css";
import bookIcon from "../../../assets/img/bookIcon.png";
import uploadIcon from "../../../assets/img/uploadIcon.png";
import expandIcon from "../../../assets/svg/ExpandIcon.svg";
import { useParams } from "react-router-dom";
import { data, options, words } from "./data";
import GenericTable from "../../GeneralTable/GenericTable";
import { useHighlights } from "../../../hooks/useHighlights";

// redux
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
  setHomeProjectId,
  fetchHomeSummary,
  fetchHomeDocuments,
  setDocsPage,
  setDocsPageSize,
} from "./home.slice";
import { clearHighlightsDirty } from "../Highlights/highlights.slice";

const { Search } = Input;

type HomeProps = { reloadKey?: number };

const Home: React.FC<HomeProps> = ({ reloadKey = 0 }) => {
  const { t } = useTranslation("docProcessing");
  const { id: projectId } = useParams();
  const dispatch = useAppDispatch();

  const columns = [
    { title: t("home.table.username"), dataIndex: "username", key: "username" },
    {
      title: t("home.table.documentName"),
      dataIndex: "documentName",
      key: "documentName",
    },
    {
      title: t("home.table.highlightLengthCount"),
      dataIndex: "highlightLength",
      key: "highlightLength",
    },
  ];

  const [cloudVisible, setCloudVisible] = useState(false);

  const summary = useAppSelector((s: any) => s.home.summary);
  const docs = useAppSelector((s: any) => s.home.docs);

  // dirty flag nga slice
  const isHighlightsDirty = useAppSelector((s: any) => s.highlights.dirty);

  // highlights hook → rifetch kur ndryshon dirty
  const { highlights, loading } = useHighlights(
    projectId,
    {},
    0,
    10,
    isHighlightsDirty,
  );

  // pastrimi i flamurit pasi u bë refetch
  useEffect(() => {
    if (isHighlightsDirty && !loading) {
      dispatch(clearHighlightsDirty());
    }
  }, [dispatch, isHighlightsDirty, loading]);

  // 1) vendos project id në redux sa herë që ndryshon
  useEffect(() => {
    dispatch(setHomeProjectId(projectId ?? null));
  }, [dispatch, projectId]);

  // (opsionale) reset pagination kur ndryshon projekti
  useEffect(() => {
    if (!projectId) return;
    dispatch(setDocsPage(1));
  }, [dispatch, projectId]);

  // 2) summary: rifresko vetëm kur ndryshon projekti ose reloadKey
  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchHomeSummary());
  }, [dispatch, projectId, reloadKey]);

  // 3) documents: NJË useEffect i vetëm
  useEffect(() => {
    if (!projectId) return;
    dispatch(fetchHomeDocuments());
  }, [dispatch, projectId, reloadKey, docs.page, docs.pageSize]);

  const rowSelection = useMemo(
    () => ({
      onChange: (_keys: React.Key[], _rows: any[]) => {},
    }),
    [],
  );

  return (
    <div className="homeMainContainer">
      <div className="boxesContainer">
        <div className="rowContainer">
          <div className="contentContainer">
            <h2 className="containerTitle">{t("home.projectTitle")}</h2>

            <div className="barsContainer">
              <div className="chartContainer">
                <p className="labelStyle">
                  {t("home.userCount", { value: summary.total_members })}
                </p>
                <Bar options={options} data={data} height={15} />
              </div>
              <div className="chartContainer">
                <p className="labelStyle">
                  {t("home.mediaCount", { value: summary.total_documents })}
                </p>
                <Bar options={options} data={data} height={15} />
              </div>
              <div className="chartContainer">
                <p className="labelStyle">
                  {t("home.highlightCount", {
                    value: summary.total_highlights,
                  })}
                </p>
                <Bar options={options} data={data} height={15} />
              </div>
              <div className="chartContainer">
                <p className="labelStyle">
                  {t("home.codeCount", { value: summary.total_codes })}
                </p>
                <Bar options={options} data={data} height={15} />
              </div>
            </div>
          </div>

          <div className="contentContainer">
            <h2 className="containerTitle ">{t("home.mediaSectionTitle")}</h2>
            <div className="tableWrapper">
              <GenericTable
                columns={columns}
                dataSource={docs.items}
                loading={docs.loading}
                pagination={{
                  current: docs.page,
                  pageSize: docs.pageSize,
                  total: docs.total,
                  onChange: (p: number, ps: number) => {
                    dispatch(setDocsPage(p));
                    dispatch(setDocsPageSize(ps));
                  },
                }}
                scroll={{ y: 300 }}
                rowSelection={{ type: "checkbox", ...rowSelection }}
              />
            </div>
          </div>
        </div>

        <div className="rowContainer">
          <div className="contentContainer highlightsContainer">
            <h2 className="containerTitle">{t("home.highlightsSectionTitle")}</h2>
            <div className="highlightsContentWrapper">
              {highlights?.map((h) => (
                <div key={h.id} className="mediaHeader highlightsItem">
                  <img src={bookIcon} alt="Highlights Icon" />
                  <div className="highlightsContent">
                    <div className="contentHeader">
                      <p>{t("home.added", { value: h.created_at.split("T")[0] })}</p>
                      <p>{t("home.username", { value: h.username })}</p>
                      <p>{t("home.codes", { value: h.codesCount })}</p>
                    </div>
                    <div className="highlightsText">
                      <h2>{t("home.resource", { value: h.document_name })}</h2>
                      <p>{h.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {cloudVisible && (
          <div className="rowContainer">
            <div className="contentContainer">
              <div className="cloudHeader">
                <h2>{t("home.codeCloudTitle")}</h2>
                <div>
                  <Search placeholder={t("home.searchColorsPlaceholder")} />
                  <img src={expandIcon} alt="expand" />
                  <img src={uploadIcon} alt="upload" />
                </div>
              </div>
              <div className="word-cloud-container">
                {words?.map((word, i) => (
                  <span
                    key={i}
                    className={`word weight-${word.value}`}
                    style={{
                      left: `${Math.random() * 80}%`,
                      top: `${Math.random() * 80}%`,
                    }}
                  >
                    {word.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
